import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { firebaseUserAtom } from '@/stores/auth';
import { queryKeys } from '@/lib/queryKeys';
import { getDay } from '@/repositories/days.repository';

/**
 * Fetch ritual completion status for a list of date keys.
 * Batches requests in groups of 30 to avoid overwhelming Firestore.
 * Query key uses a date range string for stable caching.
 */
export function useRitualDates(dates: string[]): {
  ritualCompletedDates: Set<string>;
  isLoading: boolean;
} {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const userId = firebaseUser?.uid;

  // Build a stable, compact query key from the date range
  const sorted = dates.length > 0 ? dates.slice().sort() : [];
  const rangeKey =
    sorted.length > 0 ? `${sorted[0]}..${sorted[sorted.length - 1]}:${sorted.length}` : '';

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.days.all, 'ritualDates', userId, rangeKey],
    queryFn: async () => {
      const completed = new Set<string>();
      // Batch in groups of 30 to avoid Firestore rate issues
      const BATCH_SIZE = 30;
      for (let i = 0; i < dates.length; i += BATCH_SIZE) {
        const batch = dates.slice(i, i + BATCH_SIZE);
        const days = await Promise.all(batch.map((date) => getDay(userId!, date)));
        days.forEach((day, j) => {
          if (day?.ritualCompleted) {
            completed.add(dates[i + j]);
          }
        });
      }
      return [...completed];
    },
    enabled: !!userId && dates.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes to reduce re-fetches
  });

  return {
    ritualCompletedDates: new Set(data ?? []),
    isLoading,
  };
}
